import { useEffect, useId, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { trackEvent } from "@/lib/analytics";
import { GATE_EMAIL_ERROR, GATE_MOBILE_ERROR, isEmail, normaliseAuMobile } from "@/lib/tools/validation";
import type { ToolSlug } from "@/lib/tools/calc";
import { postToolLead, writeGateToken } from "./gate-storage";

type Errors = Partial<Record<"firstName" | "email" | "mobile" | "form", string>>;

/** One capture for all six tools: first name, work email, mobile, optional business. */
export function ToolGate({ slug, inputs, onUnlocked, initialFirstName = "" }: { slug: ToolSlug; inputs: unknown; onUnlocked: (token: string) => void; initialFirstName?: string }) {
  const id = useId();
  const [firstName, setFirstName] = useState(initialFirstName);
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [business, setBusiness] = useState("");
  const [showBusiness, setShowBusiness] = useState(false);
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState("");
  const [errors, setErrors] = useState<Errors>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    trackEvent("tool_gate_view", { source_tool: slug });
  }, [slug]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const next: Errors = {};
    if (!firstName.trim()) next.firstName = "Enter your first name";
    if (!isEmail(email)) next.email = GATE_EMAIL_ERROR;
    if (!normaliseAuMobile(mobile)) next.mobile = GATE_MOBILE_ERROR;
    setErrors(next);
    if (Object.keys(next).length) return;
    setBusy(true);
    try {
      const res = await postToolLead({
        kind: "capture",
        sourceTool: slug,
        inputs,
        firstName,
        email,
        mobile,
        businessName: business,
        insightsConsent: consent,
        website,
      });
      if (!res.ok || !res.token) {
        const f = res.fields ?? {};
        setErrors({
          firstName: f.firstName?.[0],
          email: f.email?.[0],
          mobile: f.mobile?.[0],
          form: res.error ?? "Something went wrong. Please try again.",
        });
        return;
      }
      trackEvent("tool_gate_submit", { source_tool: slug });
      writeGateToken(res.token);
      onUnlocked(res.token);
    } catch {
      setErrors({ form: "Could not reach Ajility. Check your connection and try again." });
    } finally {
      setBusy(false);
    }
  }

  const field = "mt-1 h-11 bg-card text-foreground";
  const err = (k: keyof Errors) =>
    errors[k] ? (
      <p id={`${id}-${k}-err`} className="mt-1 text-sm text-destructive-on-dark">
        {errors[k]}
      </p>
    ) : null;

  return (
    <form onSubmit={submit} noValidate className="mt-6 rounded-2xl border border-white/15 bg-white/5 p-5" aria-labelledby={`${id}-h`}>
      <p id={`${id}-h`} className="flex items-center gap-2 font-semibold text-frost">
        <Lock className="h-4 w-4" aria-hidden="true" /> Unlock the full one page report and PDF
      </p>
      <p className="mt-1 text-sm text-frost/75">{slug === "partner-alignment-check" ? "Asked once. It unlocks your report and partner comparison." : "Asked once. It unlocks all six tools."}</p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor={`${id}-fn`} className="text-sm font-medium text-frost">First name</label>
          <Input id={`${id}-fn`} autoComplete="given-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} className={field} aria-invalid={!!errors.firstName} aria-describedby={errors.firstName ? `${id}-firstName-err` : undefined} maxLength={60} />
          {err("firstName")}
        </div>
        <div>
          <label htmlFor={`${id}-mb`} className="text-sm font-medium text-frost">Mobile</label>
          <Input id={`${id}-mb`} type="tel" autoComplete="tel" inputMode="tel" placeholder="04xx xxx xxx" value={mobile} onChange={(e) => setMobile(e.target.value)} className={field} aria-invalid={!!errors.mobile} aria-describedby={errors.mobile ? `${id}-mobile-err` : undefined} maxLength={30} />
          {err("mobile")}
        </div>
        <div className="sm:col-span-2">
          <label htmlFor={`${id}-em`} className="text-sm font-medium text-frost">Work email</label>
          <Input id={`${id}-em`} type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} className={field} aria-invalid={!!errors.email} aria-describedby={errors.email ? `${id}-email-err` : undefined} maxLength={255} />
          {err("email")}
        </div>
      </div>
      <div className="mt-3">
        {showBusiness ? (
          <div>
            <label htmlFor={`${id}-bz`} className="text-sm font-medium text-frost">Business name (optional)</label>
            <Input id={`${id}-bz`} autoComplete="organization" value={business} onChange={(e) => setBusiness(e.target.value)} className={field} maxLength={150} />
          </div>
        ) : (
          <button type="button" onClick={() => setShowBusiness(true)} className="text-sm font-medium text-steel-on-dark underline underline-offset-4 hover:text-frost focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-frost">
            Add your business (optional)
          </button>
        )}
      </div>
      <div className="hidden" aria-hidden="true">
        <label htmlFor={`${id}-ws`}>Website</label>
        <input id={`${id}-ws`} tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
      </div>
      <label className="mt-4 flex items-start gap-2 text-sm text-frost/85">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 h-4 w-4 accent-steel-on-dark" />
        Send me occasional Ajility insights (unsubscribe any time)
      </label>
      <p className="mt-3 text-xs text-frost/70">
        Ajility collects these details to send you your report and to contact you about it.{" "}
        <Link to="/privacy" className="underline underline-offset-2 hover:text-frost">Privacy policy</Link>.
      </p>
      {errors.form ? <p role="alert" className="mt-3 text-sm text-destructive-on-dark">{errors.form}</p> : null}
      <button type="submit" disabled={busy} className="mt-4 inline-flex min-h-12 w-full items-center justify-center rounded-full bg-frost px-6 text-sm font-semibold text-midnight-deep transition hover:bg-white disabled:opacity-60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-frost">
        {busy ? "Unlocking your report" : "Show my full report"}
      </button>
    </form>
  );
}
